import { faCircle, faLink } from "@fortawesome/pro-solid-svg-icons";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { AnimatePresence } from "framer-motion";
import React, { useMemo, useState } from "react";

import { ExternalLink } from "src/base-components/ExternalLink";
import { Icon } from "src/base-components/Icon";
import { MotionDropdownPanel } from "src/base-components/MotionDropdownPanel";
import { Pill } from "src/base-components/Pill";
import { FlowDbShallow, FlowVersionStatus } from "src/clients/flow-api";
import { DropdownItem } from "src/design-system/Dropdown";
import { groupRelatedFlows } from "src/flow/modals/utils";
import {
  DashboardPageParams,
  getBaseUrl,
  getFlowVersionsUrl,
  getUrlToAuthoringPage,
} from "src/router/urls";
import { pluralize } from "src/utils/stringUtils";
import { assertUnreachable } from "src/utils/typeUtils";
import { useParamsDecode } from "src/utils/useParamsDecode";

export const PublishedPill: React.FC<{
  size?: "base" | "sm";
  withIcon?: boolean;
}> = ({ size = "base", withIcon }) => (
  <Pill size={size} variant="green">
    {withIcon && <Pill.Icon icon={faCircle} size="xs" />}
    <Pill.Text>Published</Pill.Text>
  </Pill>
);

export const DraftPill: React.FC<{
  size?: "base" | "sm";
  withIcon?: boolean;
  dataLoc?: string;
}> = ({ size = "base", withIcon, dataLoc }) => (
  <Pill dataLoc={dataLoc} size={size} variant="yellow">
    {withIcon && <Pill.Icon icon={faCircle} size="xs" />}
    <Pill.Text>Draft</Pill.Text>
  </Pill>
);

export const ArchivedPill: React.FC<{
  size?: "base" | "sm";
  withIcon?: boolean;
  dataLoc?: string;
}> = ({ size = "base", withIcon, dataLoc }) => (
  <Pill dataLoc={dataLoc} size={size} variant="gray">
    {withIcon && <Pill.Icon icon={faCircle} size="xs" />}
    <Pill.Text>Archived</Pill.Text>
  </Pill>
);

export const VersionStatusPill: React.FC<{ status: FlowVersionStatus }> = ({
  status,
}) => {
  switch (status) {
    case FlowVersionStatus.PUBLISHED:
      return <PublishedPill withIcon />;
    case FlowVersionStatus.ARCHIVED:
      return <ArchivedPill />;
    case FlowVersionStatus.DRAFT:
      return <DraftPill />;
    default:
      assertUnreachable(status);
  }
};

export const SandboxDefaultPill: React.FC = () => (
  <Pill>
    <Pill.Text>Sandbox Default</Pill.Text>
  </Pill>
);

export const ProductionDefaultPill: React.FC = () => (
  <Pill>
    <Pill.Text>Production Default</Pill.Text>
  </Pill>
);

export const RelatedFlowsPill: React.FC<{
  flows: FlowDbShallow[];
  isParent?: boolean;
  includeVersions?: boolean;
  countOnly?: boolean;
}> = ({
  flows,
  includeVersions = false,
  isParent = false,
  countOnly = false,
}) => {
  const { wsId, orgId } = useParamsDecode<DashboardPageParams>();
  const [isOpen, setOpen] = useState<boolean>(false);
  const linkClassName =
    "font-inter-normal-13px text-gray-800 flex items-center gap-x-2";

  const [flowsOrder, groupedFlows] = useMemo(
    () => groupRelatedFlows(flows),
    [flows],
  );

  const actionText = isParent ? "Calling" : "Called by";
  const flowText = isParent ? "Child Flow" : "Parent Flow";

  return (
    <Menu
      as="div"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <MenuButton as="div">
        <Pill hovered={isOpen} variant="outlined-white">
          <Pill.Icon icon={faLink} size="md" />
          <Pill.Text>
            {countOnly
              ? flowsOrder.length
              : `${actionText} ${pluralize(flowsOrder.length, flowText)}`}
          </Pill.Text>
        </Pill>
      </MenuButton>
      <AnimatePresence initial={false}>
        {isOpen && (
          <MenuItems
            anchor={{
              to: "bottom start",
              gap: 6,
              padding: 12,
            }}
            as={MotionDropdownPanel}
            className="min-w-[var(--button-width)] cursor-default rounded-lg bg-white py-2 shadow-lg"
            static
            onClick={(event) => event.stopPropagation()}
          >
            <div className="px-4 py-2.5 text-gray-800 font-inter-semibold-13px">
              {isParent ? "Child Flows" : "Parent Flows"}
            </div>
            {flowsOrder.map((flowId) => [
              <MenuItem key={flowId} as={DropdownItem}>
                <ExternalLink
                  className={linkClassName}
                  href={getBaseUrl() + getFlowVersionsUrl(orgId, wsId, flowId)}
                >
                  {groupedFlows[flowId]?.name}
                </ExternalLink>
              </MenuItem>,
              includeVersions &&
                groupedFlows[flowId]?.versions?.map((version) => (
                  <MenuItem key={version.id} as={DropdownItem}>
                    <ExternalLink
                      className={linkClassName}
                      href={
                        getBaseUrl() +
                        getUrlToAuthoringPage(orgId, wsId, flowId, version.id)
                      }
                    >
                      <Icon
                        color={
                          version.status === FlowVersionStatus.PUBLISHED
                            ? "text-green-500"
                            : "text-yellow-500"
                        }
                        icon={faCircle}
                        size="xs"
                      />
                      <span className="font-inter-normal-13px">
                        {version.name}
                      </span>
                    </ExternalLink>
                  </MenuItem>
                )),
            ])}
          </MenuItems>
        )}
      </AnimatePresence>
    </Menu>
  );
};
